// Shared helper for the "Use my current location" button on the shop-search pages.
// Turns the browser's geolocation (lat/lng) into a US 5-digit ZIP via a Mapbox Geocoding
// v5 *reverse* lookup - the mirror image of the forward lookup the map already uses to
// recenter from a typed ZIP. No backend involved: the resulting ZIP is written into the
// existing zipCode field and the normal ZIP-based search runs unchanged.
import { isNativePlatform } from "./platform";

// Resolve the Mapbox token the same way the map components do: runtime-injected env first
// (window.__ENV__), then the build-time env var. Returns "" when unavailable.
export function resolveMapboxToken() {
  const runtimeToken =
    typeof window !== "undefined" &&
    window.__ENV__ &&
    typeof window.__ENV__.REACT_APP_MAPBOX_TOKEN === "string"
      ? window.__ENV__.REACT_APP_MAPBOX_TOKEN
      : "";
  return runtimeToken || process.env.REACT_APP_MAPBOX_TOKEN || "";
}

// Friendly, plain-hyphen messages for the geolocation PositionError codes (repo rule: no
// en/em dashes in user-facing copy).
function messageForPositionError(err) {
  switch (err && err.code) {
    case 1: // PERMISSION_DENIED
      return "Location permission denied. Enter your ZIP code instead.";
    case 2: // POSITION_UNAVAILABLE
      return "Could not determine your location. Enter your ZIP code instead.";
    case 3: // TIMEOUT
      return "Location request timed out. Enter your ZIP code instead.";
    default:
      return "Could not get your location. Enter your ZIP code instead.";
  }
}

// Promisified getCurrentPosition. Rejects with an Error carrying a user-facing message.
// Desktop location (Wi-Fi/IP based) can take a while for the first fix, so give it a
// generous timeout and accept a recently cached position. Logs the raw geolocation error
// code/message to the console so timeouts vs unavailable can be told apart in DevTools.
function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      resolve,
      (err) => {
        if (typeof console !== "undefined") {
          // eslint-disable-next-line no-console
          console.warn("[geolocateZip] geolocation failed", {
            code: err && err.code,
            message: err && err.message,
          });
        }
        reject(new Error(messageForPositionError(err)));
      },
      // High accuracy so phones use GPS for an exact fix (desktop has no GPS and is
      // unaffected). Cap the cache at 1 minute so we do not hand back a stale/coarse
      // reading and mask that accuracy; keep a generous timeout for the first GPS fix.
      { enableHighAccuracy: true, timeout: 25000, maximumAge: 60000 }
    );
  });
}

// On the native app, read coordinates through the Capacitor Geolocation plugin: it prompts
// for the OS location permission and reads GPS directly, which is more reliable than the
// WebView's navigator.geolocation. Imported dynamically so the web build carries no Capacitor
// dependency (mirrors utils/platform.js). Returns { latitude, longitude } or throws.
async function getNativeCoords() {
  const { Geolocation } = await import("@capacitor/geolocation");
  await Geolocation.requestPermissions();
  const pos = await Geolocation.getCurrentPosition({
    enableHighAccuracy: true,
    timeout: 25000,
    maximumAge: 60000,
  });
  return { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
}

// Resolve the device coordinates: native plugin on the app, browser API on the web. If the
// native path fails (e.g. an older native shell without the plugin, or a denied permission),
// fall back to the browser API, which surfaces the same friendly error messages.
async function getCoords() {
  if (isNativePlatform()) {
    try {
      return await getNativeCoords();
    } catch (err) {
      if (typeof console !== "undefined") {
        // eslint-disable-next-line no-console
        console.warn("[geolocateZip] native geolocation failed, trying web fallback", err);
      }
    }
  }
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    throw new Error("Your device does not support location. Enter your ZIP code instead.");
  }
  const position = await getCurrentPosition();
  return { latitude: position.coords.latitude, longitude: position.coords.longitude };
}

// Get the user's current location and reverse-geocode it to a US 5-digit ZIP.
// Resolves with the ZIP string; rejects with an Error whose message is safe to show.
export async function detectZipFromLocation() {
  const token = resolveMapboxToken();
  if (!token) {
    throw new Error("Location lookup is unavailable. Enter your ZIP code instead.");
  }

  const { latitude, longitude } = await getCoords();

  const res = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
      `${longitude},${latitude}`
    )}.json?types=postcode&country=us&limit=1&access_token=${encodeURIComponent(token)}`
  );
  if (!res.ok) {
    throw new Error("Could not look up your ZIP code. Enter it instead.");
  }

  const data = await res.json();
  const rawZip =
    Array.isArray(data && data.features) && data.features[0]
      ? String(data.features[0].text || "")
      : "";
  const zip = rawZip.replace(/\D/g, "").slice(0, 5);
  if (!/^\d{5}$/.test(zip)) {
    throw new Error("Could not find a ZIP code for your location. Enter it instead.");
  }
  return zip;
}
